import { request } from '@/lib/api'

/**
 * Экран интеграций (тикет #124): сводка подключений, проверка соединения, push-каналы и
 * исходящие вебхуки.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export type ConnectionKind = 'SCREEN' | 'PUSH' | 'MESSENGER' | 'STUB' | 'OTHER'

export interface Connection {
  code: string
  title: string
  kind: ConnectionKind
  /** Путь своего экрана настройки, если он есть. */
  screen?: string | null
  /** Есть ли у подключения проверка соединения. */
  checkable: boolean
  enabled: boolean
  /** Учётные данные сохранены; сами — никогда не приходят. */
  credentialsSet: boolean
  organizationsEnabled: number
  lastCheckedAt?: string | null
  lastCheckOk?: boolean | null
  lastCheckMessage?: string | null
  updatedAt?: string | null
}

export interface CheckResult {
  status: 'ok' | 'rejected' | 'unavailable'
  message?: string | null
}

export function fetchConnections(signal?: AbortSignal): Promise<Connection[]> {
  return request<{ connections?: Connection[] }>('/admin/integrations', { signal }).then((body) => body.connections ?? [])
}

/**
 * Проверка соединения. У ИИ своя точка (`/admin/integrations/ai/check`, #126) с тем же `status`,
 * остальные — общая; обе запоминают итог в сводке.
 */
export function checkConnection(code: string): Promise<CheckResult> {
  return request<CheckResult>(`/admin/integrations/${encodeURIComponent(code)}/check`, { method: 'POST', body: {} })
}

// ------------------------------------------------------------------ push (#120)

export type PushChannel = 'webpush' | 'fcm' | 'rustore'

export interface PushView {
  channel: PushChannel
  enabled: boolean
  settings?: Record<string, string> | null
  secretSet: boolean
  usable: boolean
}

export interface PushUpdate {
  enabled: boolean
  settings?: Record<string, string>
  /** `undefined` — оставить сохранённый, пустая строка — стереть. */
  secret?: string
}

export function fetchPush(signal?: AbortSignal): Promise<PushView[]> {
  return request<PushView[]>('/admin/push', { signal })
}

export function updatePush(channel: PushChannel, update: PushUpdate): Promise<PushView> {
  return request<PushView>(`/admin/push/${channel}`, { method: 'PUT', body: update })
}

export function generateVapidKeys(subject: string | null): Promise<PushView> {
  return request<PushView>('/admin/push/webpush/keys', { method: 'POST', body: subject ? { subject } : {} })
}

// ------------------------------------------------------------------ webhooks

export interface WebhookEvent {
  type: string
  aggregate: string
  label: string
}

export interface WebhookSubscription {
  id: string
  name: string
  url: string
  events: string[]
  active: boolean
  secretSet: boolean
  createdAt?: string | null
  updatedAt?: string | null
  delivered: number
  waiting: number
  lost: number
  lastEventAt?: string | null
}

export interface SubscriptionRequest {
  name: string
  url: string
  events: string[]
  active?: boolean
}

/** Единственный ответ, в котором секрет приходит открытым, — один раз. */
export interface SubscriptionWithSecret {
  subscription: WebhookSubscription
  secret: string
}

export type DeliveryStatus = 'pending' | 'sending' | 'delivered' | 'failed' | 'rejected' | 'abandoned'

export interface Delivery {
  id: string
  subscriptionId: string
  subscriptionName: string
  eventId: string
  eventType: string
  aggregateId?: string | null
  status: DeliveryStatus
  attempts: number
  lastStatusCode?: number | null
  lastError?: string | null
  createdAt: string
  attemptedAt?: string | null
  deliveredAt?: string | null
  retryAfter?: string | null
}

export interface DeliveryAttempt {
  attemptNo: number
  attemptedAt: string
  outcome: 'delivered' | 'failed' | 'rejected'
  statusCode?: number | null
  durationMs?: number | null
  detail?: string | null
}

export interface DeliveryDetail {
  delivery: Delivery
  attempts: DeliveryAttempt[]
}

export interface PingResult {
  result: 'ok' | 'rejected' | 'unavailable'
  statusCode?: number | null
  detail?: string | null
  durationMs: number
}

export function fetchWebhookCatalog(signal?: AbortSignal): Promise<WebhookEvent[]> {
  return request<{ events?: WebhookEvent[] }>('/admin/webhooks/catalog', { signal }).then((body) => body.events ?? [])
}

export function fetchSubscriptions(signal?: AbortSignal): Promise<WebhookSubscription[]> {
  return request<{ subscriptions?: WebhookSubscription[] }>('/admin/webhooks/subscriptions', { signal })
    .then((body) => body.subscriptions ?? [])
}

export function createSubscription(body: SubscriptionRequest): Promise<SubscriptionWithSecret> {
  return request<SubscriptionWithSecret>('/admin/webhooks/subscriptions', { method: 'POST', body })
}

export function updateSubscription(id: string, body: SubscriptionRequest): Promise<WebhookSubscription> {
  return request<WebhookSubscription>(`/admin/webhooks/subscriptions/${id}`, { method: 'PUT', body })
}

export function rotateSecret(id: string): Promise<SubscriptionWithSecret> {
  return request<SubscriptionWithSecret>(`/admin/webhooks/subscriptions/${id}/secret`, { method: 'POST', body: {} })
}

export function deleteSubscription(id: string): Promise<void> {
  return request<void>(`/admin/webhooks/subscriptions/${id}`, { method: 'DELETE' })
}

export function pingSubscription(id: string): Promise<PingResult> {
  return request<PingResult>(`/admin/webhooks/subscriptions/${id}/ping`, { method: 'POST', body: {} })
}

export function fetchDeliveries(subscriptionId: string | null, status: DeliveryStatus | null,
                                signal?: AbortSignal): Promise<Delivery[]> {
  const query = new URLSearchParams({ limit: '50' })
  if (subscriptionId) query.set('subscriptionId', subscriptionId)
  if (status) query.set('status', status)
  return request<{ deliveries?: Delivery[] }>(`/admin/webhooks/deliveries?${query.toString()}`, { signal })
    .then((body) => body.deliveries ?? [])
}

export function fetchDelivery(id: string, signal?: AbortSignal): Promise<DeliveryDetail> {
  return request<DeliveryDetail>(`/admin/webhooks/deliveries/${id}`, { signal })
}

export function retryDelivery(id: string): Promise<DeliveryDetail> {
  return request<DeliveryDetail>(`/admin/webhooks/deliveries/${id}/retry`, { method: 'POST', body: {} })
}
